import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import { commitMutation, createFragmentContainer, graphql } from 'react-relay';
import { Router } from 'found';
import { DelegateProfilePage_delegate } from './__generated__/DelegateProfilePage_delegate.graphql';
import moment from 'moment';
import { Page } from 'sharedComponents/Pagination/Paginator/Pageinator';
import Button from 'components/Button/Button';
import environment from 'api/environment';
import { GraphError } from 'types/general';
import { DelegateProfilePage_RequestBackgroundCheckMutationResponse } from './__generated__/DelegateProfilePage_RequestBackgroundCheckMutation.graphql';
import { useToast } from 'components/Toasts/ToastProvider';

import { DelegateProfilePage_manager } from './__generated__/DelegateProfilePage_manager.graphql';

import CourseTable from 'features/user-profile/components/CourseTable';
import DocumentsTable from 'features/user-profile/components/DocumentsTable';
import ProfileCard from 'features/user-profile/components/ProfileCard';
import BackgroundCheckCard from 'features/user-profile/components/BackgroundCheckCard';
import ProfileIcon from 'sharedComponents/core/ProfileIcon';
import { useDelegateDetailsDispatch } from 'views/DelegateDetailsProvider';
import SimulatorCard from 'features/user-profile/components/SimulatorCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import ActivityTable from 'features/user-profile/components/ActivityTable';
import { CheckType } from 'features/background-checks/components/RequestBackgroundCheckModal/RequestBackgroundCheckModal';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: '40px 40px'
  },
  top: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  divider: {
    width: theme.spacing(1)
  },
  quickOverview: {
    gridArea: 'overvw'
  },
  trainingProgress: {
    gridArea: 'traini'
  },
  cardFlex: {
    display: 'flex'
  },
  profileSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.75rem',
    background: 'white',
    border: '1px solid #E0E3E7',
    borderRadius: 8,
    padding: 25
  },
  profileGrid: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '2.5rem',
    '@media (max-width: 1200px)': {
      flexDirection: 'column',
      gap: '1.75rem'
    }
  },
  profileCol: {
    flex: 1,
    minWidth: 0
  },
  colDivider: {
    width: 1,
    alignSelf: 'stretch',
    background: '#ededed',
    '@media (max-width: 1200px)': {
      width: 'auto',
      height: 1,
      alignSelf: 'auto'
    }
  },
  headerActions: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center'
  },
  noCourses: {
    textAlign: 'center',
    fontWeight: 300,
    color: theme.colors.textGrey
  },
  courseName: {
    fontWeight: 700,
    fontSize: '0.9rem'
  },
  hasActive: {
    gap: '12px',
    color: '#18c329',
    border: '1px solid #18c329',
    display: 'flex',
    padding: '9px 15px',
    fontSize: 12,
    background: 'white',
    alignItems: 'center',
    fontWeight: 500,
    borderRadius: 24,
    height: 32
  },
  back: {},
  profileHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '2rem',
    marginTop: 24
  },
  profileHeader__info: {
    display: 'flex',
    alignItems: 'center',
    gap: '1.5rem'
  },
  profileHeader__name: {
    fontSize: '1.4rem',
    fontWeight: 700,
    margin: 0
  },
  profileHeader__job: {
    fontSize: '0.8rem',
    fontWeight: 700,
    margin: 0,
    color: '#6A728F'
  },
  tabBar: {
    display: 'flex',
    gap: 4,
    marginTop: 20,
    borderBottom: '1px solid #ededed'
  },
  tab: {
    position: 'relative',
    padding: '12px 16px',
    fontSize: 14,
    fontWeight: 500,
    color: '#74777f',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    marginBottom: -1,
    cursor: 'pointer',
    transition: 'color 0.15s',
    '&:hover': {
      color: theme.colors.primaryBlack
    }
  },
  tabActive: {
    color: '#0b57ff',
    borderBottom: '2px solid #0b57ff'
  },
  tabPanel: {
    marginTop: 24
  }
}));

const mutation = graphql`
  mutation DelegateProfilePage_RequestBackgroundCheckMutation(
    $delegateUUID: UUID!
    $email: String
    $requestDBS: Boolean!
    $isRenewal: Boolean
    $onlyDBS: Boolean
    $idExpiry: Time
    $isCAA: Boolean
  ) {
    requestBackgroundCheck(
      delegateUUID: $delegateUUID
      requestDBS: $requestDBS
      email: $email
      isRenewal: $isRenewal
      onlyDBS: $onlyDBS
      idExpiryDate: $idExpiry
      isCAA: $isCAA
    )
  }
`;

type TabKey = 'profile' | 'courses' | 'simulator' | 'documents' | 'activity';

const tabs: { key: TabKey; label: string }[] = [
  { key: 'profile', label: 'Profile' },
  { key: 'courses', label: 'Courses' },
  { key: 'simulator', label: '6x6 Training' },
  { key: 'documents', label: 'Records & Documents' },
  { key: 'activity', label: 'Activity' }
];

type Props = {
  delegate: DelegateProfilePage_delegate;
  manager: DelegateProfilePage_manager;
  retry: () => void;
  router: Router;
};

const DelegateProfilePage = ({ delegate, manager, router, retry }: Props) => {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const [activeTab, setActiveTab] = React.useState<TabKey>('profile');

  const toast = useToast();
  const dispatchEdit = useDelegateDetailsDispatch();

  const openDelegateModal = () => {
    dispatchEdit({
      type: 'show',
      uuid: delegate.uuid,
      onClose: () => window.location.reload()
    });
  };

  const requestBackgroundCheck = (
    requestDBS: Boolean,
    checkType: CheckType,
    idExpiry?: moment.Moment
  ) => {
    const variables = {
      delegateUUID: delegate.uuid,
      requestDBS: requestDBS,
      isRenewal: checkType === 'renewal',
      onlyDBS: checkType === 'onlyDBS',
      idExpiry: idExpiry,
      isCAA: checkType === 'onlyDBS' ? false : true
    };

    commitMutation(environment, {
      mutation,
      variables,
      onCompleted: async (
        response: DelegateProfilePage_RequestBackgroundCheckMutationResponse,
        errors: GraphError[]
      ) => {
        if (errors) {
          toast({
            message: {
              variant: 'error',
              text: errors?.[0]?.extensions?.helpText ?? errors?.[0]?.message
            }
          });
          console.error(errors);
          return;
        }

        if (response.requestBackgroundCheck) {
          toast({
            message: {
              variant: 'success',
              text: 'Background check requested'
            }
          });
          retry();
        }
      },
      onError: (err) => {
        console.log(err);
        toast({
          message: {
            variant: 'error',
            text:
              err?.[0]?.extensions?.helpText ?? err?.[0]?.message ?? err.message
          }
        });
      }
    });
  };

  return (
    <div className={classes.root}>
      <div className={classes.top}>
        <div className={classes.back}>
          <Button archetype="flat" onClick={() => router.go(-1)}>
            <FontAwesomeIcon icon={faChevronLeft} /> Back to delegates
          </Button>
        </div>
      </div>
      <div className={classes.profileHeader}>
        <div className={classes.profileHeader__info}>
          <ProfileIcon
            name={`${delegate.firstName} ${delegate.lastName}`}
            size={64}
            fontSize={22}
            url={delegate.profileImageUrl ?? undefined}
          />
          <div>
            <p className={classes.profileHeader__name}>
              {`${delegate.firstName} ${delegate.lastName}`}
            </p>
            <p className={classes.profileHeader__job}>
              {delegate.jobTitle ? delegate.jobTitle : '-'}
            </p>
          </div>
        </div>
      </div>
      <div className={classes.tabBar}>
        {tabs
          .filter(
            (tab) => tab.key !== 'simulator' || delegate.hasSimulatorAccess
          )
          .map((tab) => (
            <button
              key={tab.key}
              className={`${classes.tab} ${
                activeTab === tab.key ? classes.tabActive : ''
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
      </div>
      <div className={classes.tabPanel}>
        {activeTab === 'profile' && (
          <div className={classes.profileSection}>
            <div className={classes.profileGrid}>
              <div className={classes.profileCol}>
                <ProfileCard
                  delegate={delegate}
                  action={
                    <Button archetype="flat" onClick={openDelegateModal}>
                      Edit details
                    </Button>
                  }
                />
              </div>
              <div className={classes.colDivider} />
              <div className={classes.profileCol}>
                <BackgroundCheckCard
                  delegate={delegate}
                  manager={manager}
                  onRequestBackgroundCheck={requestBackgroundCheck}
                />
              </div>
            </div>
          </div>
        )}
        {activeTab === 'courses' && <CourseTable delegate={delegate} />}
        {activeTab === 'simulator' && delegate.hasSimulatorAccess && (
          <div className={classes.profileSection}>
            <SimulatorCard delegateUUID={delegate.uuid} />
          </div>
        )}
        {activeTab === 'documents' && <DocumentsTable delegate={delegate} />}
        {activeTab === 'activity' && (
          <ActivityTable delegateUUID={delegate.uuid} />
        )}
      </div>
    </div>
  );
};

const DelegateProfilePageFrag = createFragmentContainer(DelegateProfilePage, {
  delegate: graphql`
    fragment DelegateProfilePage_delegate on Delegate {
      uuid
      firstName
      lastName
      email
      jobTitle
      TTC_ID
      profileImageUrl
      currentBackgroundCheck {
        idExpiryDate
        dbsExpiryDate
      }
      hasActiveBackgroundCheck
      backgroundCheckPending
      company {
        name
      }
      hasSimulatorAccess
      ...CourseTable_delegate
      ...DocumentsTable_delegate
      ...ProfileCard_delegate
      ...BackgroundCheckCard_delegate
    }
  `,
  manager: graphql`
    fragment DelegateProfilePage_manager on Manager {
      ...BackgroundCheckCard_manager
    }
  `
});

export default DelegateProfilePageFrag;
